<template>
  <el-dialog
    v-model="visible"
    width="380px"
    :show-close="false"
    align-center
    style="border-radius: 1.5rem;"
    @close="handleLater"
  >
    <div v-if="questions" class="flex flex-col gap-[16px]">
      <div v-for="q in questions" :key="q.questionKey">
        <p class="mb-[8px] text-[14px] font-bold text-osn-dark-grey-00">{{ q.text }}</p>
        <el-radio-group v-model="selections[q.questionKey]">
          <el-radio v-for="opt in q.options" :key="opt.code" :label="opt.code">{{ opt.label }}</el-radio>
        </el-radio-group>
        <el-input
          v-if="q.optionalTextAllowed && selections[q.questionKey] === 'other'"
          v-model="optionalTexts[q.questionKey]"
          type="textarea"
          :rows="2"
          maxlength="500"
          class="mt-[8px]"
        />
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-[8px]">
        <button
          type="button"
          class="rounded-full border border-osn-grey-03 px-[16px] py-[8px] text-[14px] text-osn-dark-grey-00"
          @click="handleDecline"
        >
          不想回答
        </button>
        <button
          type="button"
          class="rounded-full border border-osn-grey-03 px-[16px] py-[8px] text-[14px] text-osn-dark-grey-00"
          @click="handleLater"
        >
          稍後
        </button>
        <button
          type="button"
          class="rounded-full bg-osn-yellow-00 px-[16px] py-[8px] text-[14px] font-bold text-osn-dark-grey-00"
          :disabled="!allAnswered || submitting"
          @click="handleSubmit"
        >
          送出
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
/**
 * BI P3：站內答題全域元件，同 `ConsentBanner.vue` 的「全域掛在 app.vue、自行決定
 * 要不要顯示」模式（見 docs/behavior-insights/implementation-p3.md §1.2、§2）。
 * 掛載時（與之後每 `FEEDBACK_POLL_INTERVAL_MS` 節流一次）呼叫 `GET /next`；沒有
 * 待答項目、或 campaign 不在字典裡，一律不顯示任何東西、不拋錯。
 *
 * 僅支援登入租客：目前呼叫 next/answer/decline 都不帶 `token`，匿名／case-only
 * 租客不會看到任何提示（`getToken()` 不存在時後端當匿名處理，但後端目前沒有任何
 * 路徑把 raw token 交給客戶端，接上也無意義，見 implementation-p3.md §2 2026-09-28
 * 更正段落——本輪明確延後，非本檔漏做）。
 */
import {computed, onBeforeUnmount, onMounted, reactive, ref} from 'vue'
import {getNextFeedback, postFeedbackAnswer, postFeedbackDecline} from '@/api/feedback'
import type {FeedbackQuestion} from '@/composables/useFeedbackCatalog'
import {FEEDBACK_POLL_INTERVAL_MS, buildAnswerItems, resolveFeedbackQuestions, shouldCheckFeedback} from '@/composables/useFeedbackPrompt'

const visible = ref(false)
const questions = ref<FeedbackQuestion[] | null>(null)
const requestId = ref<number | null>(null)
const selections = reactive<Record<string, string>>({})
const optionalTexts = reactive<Record<string, string>>({})
const submitting = ref(false)

let lastCheckedAt: number | null = null
let pollTimer: ReturnType<typeof setInterval> | undefined

const allAnswered = computed(() => {
  if (!questions.value) return false
  return questions.value.every((q) => !!selections[q.questionKey])
})

function resetSelections(resolved: FeedbackQuestion[]): void {
  for (const key of Object.keys(selections)) delete selections[key]
  for (const key of Object.keys(optionalTexts)) delete optionalTexts[key]
  for (const q of resolved) {
    selections[q.questionKey] = ''
    optionalTexts[q.questionKey] = ''
  }
}

function closePrompt(): void {
  visible.value = false
  questions.value = null
  requestId.value = null
}

async function checkFeedback(): Promise<void> {
  const now = Date.now()
  if (!shouldCheckFeedback(lastCheckedAt, now)) return
  lastCheckedAt = now

  const next = await getNextFeedback()
  if (!next || next.request_ref === null) return

  const resolved = resolveFeedbackQuestions(next.campaign, next.question_version)
  if (!resolved) return // 不認得的 campaign：安全閥，不顯示、不報錯

  questions.value = resolved
  requestId.value = next.request_ref
  resetSelections(resolved)
  visible.value = true
}

async function handleSubmit(): Promise<void> {
  if (!allAnswered.value || requestId.value === null || submitting.value) return
  submitting.value = true
  const answers = buildAnswerItems(selections, optionalTexts)
  await postFeedbackAnswer(requestId.value, answers)
  submitting.value = false
  closePrompt()
}

async function handleDecline(): Promise<void> {
  if (requestId.value !== null) {
    await postFeedbackDecline(requestId.value)
  }
  closePrompt()
}

// 稍後：單純關閉，不呼叫任何 API——下次節流時間到會再查到同一筆
// （feedback_requests 狀態沒變）。
function handleLater(): void {
  closePrompt()
}

onMounted(() => {
  checkFeedback()
  pollTimer = setInterval(checkFeedback, FEEDBACK_POLL_INTERVAL_MS)
})

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})
</script>
